import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import * as R from 'ramda';

import { hasNoValue } from '../../util';
import { InputSize } from '../input';

@Component({
  selector: 'pcs-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectComponent implements OnChanges {
  @Input() label;
  @Input() labelDetails;
  @Input() inputId;
  @Input() name = '';
  @Input() disabled = false;
  @Input() readonly = false;
  @Input() inputSize: InputSize;
  @Input() labelSize: number;
  @Input() searchTerm = '';
  @Input() tabindex;
  @Input() placeholder = '';
  @Input() loading = false;
  @Input() tooltipForValue: boolean;
  @Input() focused = false;

  @HostBinding('class.row')
  @HostBinding('class.g-0')
  @Input() horizontal;

  @Output() selectChange = new EventEmitter<any>();
  @Output() selectKeyPress = new EventEmitter();
  @Output() selectKeyDown = new EventEmitter();
  @Output() selectBlur = new EventEmitter<any>();
  @Output() selectFocus = new EventEmitter();

  @ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;

  @HostBinding('class.form-group-sm')
  get isSmall() {
    return this.inputSize === InputSize.Small;
  }

  @HostBinding('class.form-group-lg')
  get isInputLarge() {
    return this.inputSize === InputSize.Large;
  }

  get labelClasses() {
    return {
      [`col-sm-${this.labelSize} col-form-label horizontal-label`]: this.horizontal,
      'w-100': !this.horizontal,
      'col-form-label-sm': this.isSmall,
      'fw-bold': this.readonly
    };
  }

  get inputWrapperClasses() {
    return {
      col: this.horizontal,
      focused: this.focused
    };
  }

  get inputClasses() {
    return {
      '': this.isDefault(),
      'form-control-sm': this.isSmall,
      'form-control-lg': this.isLarge(),
      'form-control': !this.readonly,
      'form-control-plaintext': this.readonly
    };
  }

  @HostBinding('class.mb-half')
  private get editAndSmall() {
    return !this.readonly && this.isSmall;
  }

  @HostBinding('class.mb-0')
  private get readOnlyAndSmall() {
    return this.readonly && this.isSmall;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.focused && changes.focused.previousValue && !changes.focused.currentValue) {
      this.inputRef.nativeElement.blur();
    }
  }

  showTooltip() {
    return this.tooltipForValue && this.readonly && !this.isDefaultPlaceholder();
  }

  private isDefault() {
    return this.inputSize === InputSize.Default;
  }

  private isLarge() {
    return this.inputSize === InputSize.Large;
  }

  private isDefaultPlaceholder() {
    return hasNoValue(this.placeholder) || R.equals('placeholder.startTyping', this.placeholder);
  }
}
